import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * ImageComposer
 * -------------
 * Pure client-side (Canvas 2D + optional @imgly/background-removal WASM) ürün
 * görseli işleme stüdyosu.
 *
 * Girdi: bir File (jpeg/png/webp/heic — tarayıcı ne decode ederse)
 * Çıktı: 1024×1024 JPEG Blob (quality 0.92) — upload için hazır.
 *
 * Katmanlar (en alttan en üste):
 *   1. Arka plan: ya orijinal fotoğrafın bulanıklaştırılmış versiyonu (cover-fit),
 *      ya düz krem (#FAF7F2), ya da AI ile fon silinip krem üzerine oturan sürüm.
 *   2. Ön plan (ürün): kullanıcı sürükle/zoom ile konumlandırır. AI modu açıksa,
 *      ürün görseli yerine fon-silinmiş sürüm çizilir.
 *   3. Kurumsal filigran: /logo-ikizler.png — kullanıcı seçtiği KÖŞE'de veya merkezde,
 *      ÜRÜNÜN ÜSTÜNDE (user feedback: "aksi halde işe yaramaz"). Opasite slider ile.
 *
 * Mobil & masaüstü dostu: pointer events (touch + mouse birleşik).
 */
const OUT_SIZE = 1024;
const BRAND_CREAM = "#FAF7F2";
const LOGO_PATH = "/logo-ikizler.png";

const LOGO_POSITIONS = [
  { k: "center", label: "Merkez" },
  { k: "tl", label: "Sol Üst" },
  { k: "tr", label: "Sağ Üst" },
  { k: "bl", label: "Sol Alt" },
  { k: "br", label: "Sağ Alt" },
];

// ---- Composer preset'leri — tarayıcıda sakla (localStorage) ----
const PREFS_KEY = "admin.composer.prefs.v1";
export const COMPOSER_DEFAULTS = {
  bgMode: "blur",
  blurAmount: 28,
  logoPos: "br",
  logoOpacity: 0.35,
  // AI varsayılan olarak açık mı? Composer her açıldığında bgMode="ai" seçilsin.
  defaultAi: false,
};
export function loadComposerPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...COMPOSER_DEFAULTS };
    return { ...COMPOSER_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...COMPOSER_DEFAULTS };
  }
}
export function saveComposerPrefs(p) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch {}
}

export default function ImageComposer({ open, file, onCancel, onReady }) {
  const initialPrefs = React.useMemo(() => loadComposerPrefs(), []);
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [bgRemovedImg, setBgRemovedImg] = useState(null); // AI ile fon silinmiş HTMLImageElement
  const [logoImg, setLogoImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiStatus, setAiStatus] = useState(""); // "indiriliyor…" / "işleniyor…"
  // Transform state — ilk yüklemede localStorage'daki preset'leri uygula
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [bgMode, setBgMode] = useState(initialPrefs.defaultAi ? "ai" : initialPrefs.bgMode);
  const [blurAmount, setBlurAmount] = useState(initialPrefs.blurAmount);
  const [logoOpacity, setLogoOpacity] = useState(initialPrefs.logoOpacity);
  const [logoPos, setLogoPos] = useState(initialPrefs.logoPos);
  const [logoSize, setLogoSize] = useState(initialPrefs.logoPos === "center" ? 0.55 : 0.22);
  const [savedToast, setSavedToast] = useState(false);
  const dragRef = useRef({ dragging: false, lastX: 0, lastY: 0 });

  // Load product image when file changes
  useEffect(() => {
    if (!open || !file) return;
    setBgRemovedImg(null);
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      setImg(im);
      const target = OUT_SIZE * 0.88;
      const sc = target / Math.max(im.width, im.height);
      setScale(sc);
      setOffset({ x: 0, y: 0 });
    };
    im.onerror = () => toast.error("Görsel okunamadı");
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [open, file]);

  // Load logo once
  useEffect(() => {
    const im = new Image();
    im.onload = () => setLogoImg(im);
    im.src = LOGO_PATH;
  }, []);

  // When admin picks AI mode, run @imgly/background-removal
  const runBgRemoval = useCallback(async () => {
    if (!file) return;
    if (bgRemovedImg) return; // already done for this file
    setAiBusy(true);
    setAiStatus("AI yükleniyor (ilk kullanımda ~90MB model indirilir, sonra önbellekten)");
    try {
      const mod = await import("@imgly/background-removal");
      const removeBackground = mod.default || mod.removeBackground;
      setAiStatus("Arka plan siliniyor…");
      const blob = await removeBackground(file, {
        progress: (key, current, total) => {
          if (total && current <= total) {
            const pct = Math.round((current / total) * 100);
            setAiStatus(`${key} · %${pct}`);
          }
        },
      });
      const url = URL.createObjectURL(blob);
      const im = new Image();
      im.onload = () => {
        setBgRemovedImg(im);
        setAiStatus("");
        setAiBusy(false);
      };
      im.onerror = () => {
        setAiStatus("");
        setAiBusy(false);
        toast.error("AI çıktısı yüklenemedi");
      };
      im.src = url;
    } catch (e) {
      console.error(e);
      setAiStatus("");
      setAiBusy(false);
      toast.error("AI arka plan silme başarısız — bulanık moda dönülüyor");
      setBgMode("blur");
    }
  }, [file, bgRemovedImg]);

  useEffect(() => {
    if (bgMode === "ai" && !bgRemovedImg && !aiBusy) {
      runBgRemoval();
    }
  }, [bgMode, bgRemovedImg, aiBusy, runBgRemoval]);

  // Helper: compute logo rect based on position
  const computeLogoRect = (lw, lh) => {
    const pad = 32; // kenardan boşluk (px, canvas uzayı)
    switch (logoPos) {
      case "tl": return { x: pad, y: pad };
      case "tr": return { x: OUT_SIZE - lw - pad, y: pad };
      case "bl": return { x: pad, y: OUT_SIZE - lh - pad };
      case "br": return { x: OUT_SIZE - lw - pad, y: OUT_SIZE - lh - pad };
      case "center":
      default:   return { x: (OUT_SIZE - lw) / 2, y: (OUT_SIZE - lh) / 2 };
    }
  };

  // Redraw canvas whenever anything changes
  const draw = useCallback(() => {
    const cvs = canvasRef.current;
    if (!cvs || !img) return;
    const ctx = cvs.getContext("2d");
    ctx.clearRect(0, 0, OUT_SIZE, OUT_SIZE);

    // Layer 1: BACKGROUND
    if (bgMode === "blur") {
      ctx.save();
      ctx.filter = `blur(${blurAmount}px) brightness(0.9)`;
      const r = Math.max(OUT_SIZE / img.width, OUT_SIZE / img.height) * 1.12;
      const bw = img.width * r;
      const bh = img.height * r;
      ctx.drawImage(img, (OUT_SIZE - bw) / 2, (OUT_SIZE - bh) / 2, bw, bh);
      ctx.restore();
      ctx.save();
      ctx.fillStyle = "rgba(250, 247, 242, 0.42)";
      ctx.fillRect(0, 0, OUT_SIZE, OUT_SIZE);
      ctx.restore();
    } else {
      // cream OR ai → tek renk krem zemin (ai için ürün zaten transparan oturacak)
      ctx.fillStyle = BRAND_CREAM;
      ctx.fillRect(0, 0, OUT_SIZE, OUT_SIZE);
    }

    // Layer 2: FOREGROUND (ürün)
    // AI modunda ve çıktı hazırsa, bg-silinmiş görseli; aksi halde orijinali kullan
    const source = bgMode === "ai" && bgRemovedImg ? bgRemovedImg : img;
    const w = source.width * scale;
    const h = source.height * scale;
    const x = (OUT_SIZE - w) / 2 + offset.x;
    const y = (OUT_SIZE - h) / 2 + offset.y;
    ctx.save();
    // AI modunda ürün transparan — gölge yerine çok hafif drop shadow kalsın
    ctx.shadowColor = bgMode === "ai" ? "rgba(27, 42, 34, 0.18)" : "rgba(27, 42, 34, 0.25)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 8;
    ctx.drawImage(source, x, y, w, h);
    ctx.restore();

    // Layer 3: LOGO WATERMARK (ÜRÜNÜN ÜSTÜNDE — user feedback)
    if (logoImg && logoOpacity > 0) {
      ctx.save();
      ctx.globalAlpha = logoOpacity;
      const lw = Math.min(OUT_SIZE * logoSize, logoImg.width);
      const lh = (lw / logoImg.width) * logoImg.height;
      const { x: lx, y: ly } = computeLogoRect(lw, lh);
      ctx.drawImage(logoImg, lx, ly, lw, lh);
      ctx.restore();
    }
  }, [img, bgRemovedImg, logoImg, scale, offset, bgMode, blurAmount, logoOpacity, logoPos, logoSize]);

  useEffect(() => { draw(); }, [draw]);

  // Pointer drag (mouse + touch unified)
  const onPointerDown = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (OUT_SIZE / rect.width);
    const py = (e.clientY - rect.top) * (OUT_SIZE / rect.height);
    dragRef.current = { dragging: true, lastX: px, lastY: py };
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (OUT_SIZE / rect.width);
    const py = (e.clientY - rect.top) * (OUT_SIZE / rect.height);
    const dx = px - dragRef.current.lastX;
    const dy = py - dragRef.current.lastY;
    dragRef.current.lastX = px;
    dragRef.current.lastY = py;
    setOffset((o) => ({ x: o.x + dx, y: o.y + dy }));
  };
  const onPointerUp = (e) => {
    dragRef.current.dragging = false;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch {}
  };
  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setScale((s) => Math.max(0.2, Math.min(3, s * (1 + delta))));
  };

  const reset = () => {
    if (!img) return;
    const target = OUT_SIZE * 0.88;
    setScale(target / Math.max(img.width, img.height));
    setOffset({ x: 0, y: 0 });
  };

  const exportBlob = () => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    setBusy(true);
    cvs.toBlob((blob) => {
      setBusy(false);
      if (!blob) {
        toast.error("Dışa aktarma başarısız");
        return;
      }
      onReady && onReady(blob);
    }, "image/jpeg", 0.92);
  };

  // Köşe seçildiğinde logo'yu makul boyuta çek; merkezde biraz daha büyük
  useEffect(() => {
    if (logoPos === "center") setLogoSize(0.55);
    else setLogoSize(0.22);
  }, [logoPos]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy && !aiBusy) onCancel && onCancel(); }}>
      <DialogContent
        data-testid="image-composer-dialog"
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-xl max-h-[94vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            🎨 Ürün Fotoğrafı Düzenle
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sürükle · yakınlaş · arka plan modunu seç · logoyu konumlandır. Son 1024×1024 kaydedilir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Canvas preview */}
          <div className="relative w-full aspect-square max-h-[62vh] rounded-2xl overflow-hidden border-2 border-[#2E5A36]/30 bg-[#E8E2D5] shadow-inner">
            <canvas
              ref={canvasRef}
              width={OUT_SIZE}
              height={OUT_SIZE}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onWheel={onWheel}
              data-testid="image-composer-canvas"
              className="w-full h-full touch-none select-none cursor-grab active:cursor-grabbing"
              style={{ touchAction: "none" }}
            />
            {!img && (
              <div className="absolute inset-0 flex items-center justify-center text-[#1B2A22]/50 text-sm">
                Görsel yükleniyor...
              </div>
            )}
            {aiBusy && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm text-center p-6 backdrop-blur-sm">
                <div className="space-y-2">
                  <div className="text-2xl animate-spin">🤖</div>
                  <div className="font-heading font-bold">AI çalışıyor</div>
                  <div className="text-xs opacity-90">{aiStatus}</div>
                </div>
              </div>
            )}
          </div>

          {/* Zoom slider */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <Label className="text-[#1B2A22]/70">🔍 Yakınlaştır / Uzaklaştır</Label>
              <span className="font-mono text-[#1B2A22]/60">{scale.toFixed(2)}×</span>
            </div>
            <Slider
              data-testid="composer-zoom"
              min={0.2} max={3} step={0.01}
              value={[scale]}
              onValueChange={(v) => setScale(v[0])}
            />
          </div>

          {/* Background mode */}
          <div className="space-y-1">
            <Label className="text-xs text-[#1B2A22]/70">🖼 Arka Plan Modu</Label>
            <div className="grid grid-cols-3 gap-2">
              <label className={`flex items-center justify-center gap-1 p-2 rounded-xl border cursor-pointer text-xs font-heading ${bgMode === "blur" ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "bg-white border-[#E8E2D5]"}`}>
                <input type="radio" name="bg" checked={bgMode === "blur"} onChange={() => setBgMode("blur")} className="hidden" data-testid="composer-bg-blur" />
                🫧 Bulanık
              </label>
              <label className={`flex items-center justify-center gap-1 p-2 rounded-xl border cursor-pointer text-xs font-heading ${bgMode === "cream" ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "bg-white border-[#E8E2D5]"}`}>
                <input type="radio" name="bg" checked={bgMode === "cream"} onChange={() => setBgMode("cream")} className="hidden" data-testid="composer-bg-cream" />
                🎨 Krem
              </label>
              <label className={`flex items-center justify-center gap-1 p-2 rounded-xl border cursor-pointer text-xs font-heading ${bgMode === "ai" ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "bg-white border-[#E8E2D5]"}`}>
                <input type="radio" name="bg" checked={bgMode === "ai"} onChange={() => setBgMode("ai")} className="hidden" data-testid="composer-bg-ai" />
                🤖 AI Sil
              </label>
            </div>
            {bgMode === "blur" && (
              <div className="pt-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#1B2A22]/60">Bulanıklık</span>
                  <span className="font-mono text-[#1B2A22]/60">{blurAmount}px</span>
                </div>
                <Slider
                  data-testid="composer-blur"
                  min={4} max={60} step={1}
                  value={[blurAmount]}
                  onValueChange={(v) => setBlurAmount(v[0])}
                />
              </div>
            )}
            {bgMode === "ai" && (
              <div className="pt-1 text-[11px] text-[#1B2A22]/60 bg-[#2E5A36]/5 border border-[#2E5A36]/20 rounded-lg p-2">
                🤖 Yapay zeka ürünün arkasındaki fonu siler ve krem kurumsal şablon üzerine oturtur.<br/>
                İlk kullanımda model indirileceği için biraz zaman alır — sonrakiler saniyeler içinde.
              </div>
            )}
          </div>

          {/* Logo position + opacity */}
          <div className="space-y-1">
            <Label className="text-xs text-[#1B2A22]/70">🏷 Logo Konumu & Opasite</Label>
            <div className="grid grid-cols-5 gap-1">
              {LOGO_POSITIONS.map((p) => (
                <label
                  key={p.k}
                  className={`flex items-center justify-center p-2 rounded-xl border cursor-pointer text-[11px] font-heading ${logoPos === p.k ? "bg-[#D9653B] text-white border-[#D9653B]" : "bg-white border-[#E8E2D5]"}`}
                >
                  <input type="radio" name="logopos" checked={logoPos === p.k} onChange={() => setLogoPos(p.k)} className="hidden" data-testid={`composer-logo-pos-${p.k}`} />
                  {p.label}
                </label>
              ))}
            </div>
            <div className="pt-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#1B2A22]/60">Opasite (ürünün ÜSTÜNDE)</span>
                <span className="font-mono text-[#1B2A22]/60">%{Math.round(logoOpacity * 100)}</span>
              </div>
              <Slider
                data-testid="composer-logo-opacity"
                min={0} max={1} step={0.01}
                value={[logoOpacity]}
                onValueChange={(v) => setLogoOpacity(v[0])}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1 flex-wrap">
            <Button variant="outline" onClick={reset} className="rounded-full" disabled={busy || aiBusy || !img} data-testid="composer-reset-btn">
              ↺ Sıfırla
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                saveComposerPrefs({
                  bgMode,
                  blurAmount,
                  logoPos,
                  logoOpacity,
                  defaultAi: bgMode === "ai",
                });
                setSavedToast(true);
                toast.success("✓ Mevcut ayarlar varsayılan olarak kaydedildi");
                setTimeout(() => setSavedToast(false), 1500);
              }}
              disabled={busy || aiBusy}
              className={`rounded-full font-heading ${savedToast ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "border-[#1B2A22] text-[#1B2A22]"}`}
              data-testid="composer-save-defaults-btn"
              title="Logo konumu, opasite, arka plan modu ve AI'yı varsayılan yap"
            >
              💾 {savedToast ? "Kaydedildi" : "Varsayılan Yap"}
            </Button>
            <Button variant="outline" onClick={() => onCancel && onCancel()} className="rounded-full" disabled={busy || aiBusy} data-testid="composer-cancel-btn">
              İptal
            </Button>
            <Button
              onClick={exportBlob}
              disabled={busy || aiBusy || !img}
              className="flex-1 bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full font-heading font-bold"
              data-testid="composer-apply-btn"
            >
              {busy ? "Hazırlanıyor..." : aiBusy ? "AI çalışıyor..." : "✓ Kullan & Yükle"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
